import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import MetricCard from './components/MetricCard';
import AlertFeed from './components/AlertFeed';
import FacilityMap from './components/FacilityMap';
import EquipmentGrid from './components/EquipmentGrid';
import ControlPanel from './components/ControlPanel';

const OperationsCommandCenter = () => {
  const [isLiveMode, setIsLiveMode] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock data for metrics
  const metricsData = [
    {
      title: "Warehouse Efficiency",
      value: "94.2",
      unit: "%",
      change: "+2.1%",
      changeType: "positive",
      icon: "Package",
      status: "excellent",
      sparklineData: [85, 87, 89, 92, 94, 96, 94, 95]
    },
    {
      title: "Fleet Utilization",
      value: "87.5",
      unit: "%",
      change: "-1.3%",
      changeType: "negative",
      icon: "Truck",
      status: "good",
      sparklineData: [90, 89, 88, 87, 86, 87, 88, 87]
    },
    {
      title: "Asset Availability",
      value: "91.8",
      unit: "%",
      change: "+0.5%",
      changeType: "positive",
      icon: "Settings",
      status: "good",
      sparklineData: [88, 89, 90, 91, 92, 91, 92, 91]
    },
    {
      title: "Safety Incidents",
      value: "2",
      unit: "today",
      change: "-1",
      changeType: "positive",
      icon: "Shield",
      status: "warning",
      sparklineData: [5, 4, 3, 4, 3, 2, 3, 2]
    },
    {
      title: "Energy Consumption",
      value: "1,247",
      unit: "kWh",
      change: "+3.2%",
      changeType: "negative",
      icon: "Zap",
      status: "warning",
      sparklineData: [1200, 1210, 1220, 1230, 1240, 1245, 1247, 1247]
    },
    {
      title: "Carbon Footprint",
      value: "2.3",
      unit: "tons CO₂",
      change: "-5.1%",
      changeType: "positive",
      icon: "Leaf",
      status: "excellent",
      sparklineData: [2.8, 2.7, 2.6, 2.5, 2.4, 2.3, 2.3, 2.3]
    }
  ];

  // Mock data for alerts
  const alertsData = [
    {
      id: 1,
      title: "Forklift FL-003 Maintenance Required",
      description: "Hydraulic pressure below optimal threshold. Schedule maintenance within 24 hours.",
      severity: "warning",
      category: "equipment",
      location: "Warehouse A - Zone 3",
      timestamp: new Date(Date.now() - 300000),
      isRead: false
    },
    {
      id: 2,
      title: "Vehicle V-127 Route Deviation",
      description: "Driver has deviated from optimal route by 15 minutes. Fuel efficiency impacted.",
      severity: "info",
      category: "fleet",
      location: "Route 45 - Mile 23",
      timestamp: new Date(Date.now() - 600000),
      isRead: false
    },
    {
      id: 3,
      title: "Temperature Alert - Cold Storage Unit 2",
      description: "Temperature rising above acceptable range. Immediate attention required.",
      severity: "critical",
      category: "warehouse",
      location: "Cold Storage - Unit 2",
      timestamp: new Date(Date.now() - 900000),
      isRead: false
    },
    {
      id: 4,
      title: "Safety Incident Reported",
      description: "Minor slip incident reported in loading dock area. First aid administered.",
      severity: "warning",
      category: "safety",
      location: "Loading Dock B",
      timestamp: new Date(Date.now() - 1800000),
      isRead: true
    },
    {
      id: 5,
      title: "Energy Consumption Spike",
      description: "Unusual energy consumption pattern detected in manufacturing line 3.",
      severity: "info",
      category: "energy",
      location: "Manufacturing Line 3",
      timestamp: new Date(Date.now() - 2700000),
      isRead: true
    }
  ];

  // Mock data for assets
  const assetsData = [
    { id: 'A001', name: 'Forklift FL-001', type: 'forklift', status: 'active', x: 25, y: 30 },
    { id: 'A002', name: 'Forklift FL-002', type: 'forklift', status: 'idle', x: 35, y: 45 },
    { id: 'A003', name: 'Conveyor CV-001', type: 'conveyor', status: 'active', x: 50, y: 25 },
    { id: 'A004', name: 'Robot RB-001', type: 'robot', status: 'active', x: 65, y: 35 },
    { id: 'A005', name: 'Scanner SC-001', type: 'scanner', status: 'maintenance', x: 75, y: 55 },
    { id: 'A006', name: 'Dock Door DD-001', type: 'dock', status: 'active', x: 20, y: 80 }
  ];

  // Mock data for vehicles
  const vehiclesData = [
    { id: 'V001', type: 'truck', status: 'moving', x: 15, y: 85 },
    { id: 'V002', type: 'van', status: 'idle', x: 85, y: 85 },
    { id: 'V003', type: 'truck', status: 'moving', x: 45, y: 90 },
    { id: 'V004', type: 'forklift', status: 'maintenance', x: 90, y: 75 }
  ];

  // Mock data for map alerts
  const mapAlertsData = [
    { id: 'MA001', title: 'Temperature Alert', severity: 'critical', x: 75, y: 25 },
    { id: 'MA002', title: 'Maintenance Due', severity: 'warning', x: 35, y: 65 }
  ];

  // Mock data for equipment
  const equipmentData = [
    {
      id: 'EQ001',
      name: 'Forklift FL-001',
      type: 'Material Handling',
      location: 'Warehouse A',
      status: 'active',
      utilization: 87.5,
      lastMaintenance: '2024-07-15',
      nextMaintenance: '2024-08-20',
      icon: 'Forklift'
    },
    {
      id: 'EQ002',
      name: 'Conveyor Belt CB-001',
      type: 'Automation',
      location: 'Production Line 1',
      status: 'active',
      utilization: 94.2,
      lastMaintenance: '2024-07-20',
      nextMaintenance: '2024-08-25',
      icon: 'ArrowRight'
    },
    {
      id: 'EQ003',
      name: 'Robotic Arm RA-001',
      type: 'Automation',
      location: 'Assembly Station 3',
      status: 'maintenance',
      utilization: 0,
      lastMaintenance: '2024-08-10',
      nextMaintenance: '2024-08-17',
      icon: 'Bot'
    },
    {
      id: 'EQ004',
      name: 'Scanner SC-002',
      type: 'Data Collection',
      location: 'Warehouse B',
      status: 'idle',
      utilization: 45.8,
      lastMaintenance: '2024-07-25',
      nextMaintenance: '2024-08-30',
      icon: 'Scan'
    },
    {
      id: 'EQ005',
      name: 'Compressor CP-001',
      type: 'Utility',
      location: 'Utility Room',
      status: 'active',
      utilization: 78.3,
      lastMaintenance: '2024-07-10',
      nextMaintenance: '2024-08-15',
      icon: 'Wind'
    },
    {
      id: 'EQ006',
      name: 'Crane CR-001',
      type: 'Material Handling',
      location: 'Loading Dock',
      status: 'offline',
      utilization: 0,
      lastMaintenance: '2024-08-05',
      nextMaintenance: '2024-08-12',
      icon: 'Crane'
    }
  ];

  // Auto refresh effect
  useEffect(() => {
    if (isLiveMode && autoRefresh) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
        // Simulate connection status changes occasionally
        if (Math.random() < 0.05) {
          setConnectionStatus('connecting');
          setTimeout(() => setConnectionStatus('connected'), 2000);
        }
      }, refreshInterval * 1000);

      return () => clearInterval(interval);
    }
  }, [isLiveMode, autoRefresh, refreshInterval]);

  const handleMetricClick = (metric) => {
    console.log('Metric clicked:', metric);
  };

  const handleAlertClick = (alert) => {
    console.log('Alert clicked:', alert);
  };

  const handleMarkAsRead = (alertId) => {
    console.log('Mark as read:', alertId);
  };

  const handleFilterChange = (filters) => {
    console.log('Filter changed:', filters);
  };

  const handleAssetClick = (asset) => {
    console.log('Asset clicked:', asset);
  };

  const handleVehicleClick = (vehicle) => {
    console.log('Vehicle clicked:', vehicle);
  };

  const handleMapAlertClick = (alert) => {
    console.log('Map alert clicked:', alert);
  };

  const handleEquipmentAction = (equipment, action) => {
    console.log('Equipment action:', equipment, action);
  };

  const handleMaintenanceSchedule = (equipment) => {
    console.log('Schedule maintenance:', equipment);
  };

  const handleToggleLiveMode = () => {
    setIsLiveMode(!isLiveMode);
  };

  const handleToggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  const handleRefreshIntervalChange = (interval) => {
    setRefreshInterval(interval);
  };

  const handleManualRefresh = () => {
    setConnectionStatus('connecting');
    setTimeout(() => {
      setConnectionStatus('connected');
      setLastUpdate(new Date());
    }, 1000);
  };

  const handleExportData = () => {
    console.log('Exporting data...');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-full mx-auto p-6">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Operations Command Center</h1>
                <p className="text-muted-foreground mt-1">
                  Real-time monitoring and control of manufacturing operations
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <div className={`status-indicator ${connectionStatus === 'connected' ? 'status-online' : connectionStatus === 'connecting' ? 'status-warning' : 'status-offline'}`}></div>
                <span className="text-sm text-muted-foreground">
                  {connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Connecting...' : 'Offline'}
                </span>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
            {metricsData?.map((metric, index) => (
              <MetricCard
                key={index}
                {...metric}
                onClick={() => handleMetricClick(metric)}
              />
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-8">
            {/* Facility Map */}
            <div className="xl:col-span-3">
              <FacilityMap
                assets={assetsData}
                vehicles={vehiclesData}
                alerts={mapAlertsData}
                onAssetClick={handleAssetClick}
                onVehicleClick={handleVehicleClick}
                onAlertClick={handleMapAlertClick}
              />
            </div>

            {/* Right Sidebar */}
            <div className="xl:col-span-1 space-y-6">
              {/* Alert Feed */}
              <div className="h-96">
                <AlertFeed
                  alerts={alertsData}
                  onAlertClick={handleAlertClick}
                  onMarkAsRead={handleMarkAsRead}
                  onFilterChange={handleFilterChange}
                />
              </div>

              {/* Control Panel */}
              <ControlPanel
                isLiveMode={isLiveMode}
                onToggleLiveMode={handleToggleLiveMode}
                autoRefresh={autoRefresh}
                onToggleAutoRefresh={handleToggleAutoRefresh}
                refreshInterval={refreshInterval}
                onRefreshIntervalChange={handleRefreshIntervalChange}
                onManualRefresh={handleManualRefresh}
                onExportData={handleExportData}
                connectionStatus={connectionStatus}
                lastUpdate={lastUpdate}
              />
            </div>
          </div>

          {/* Equipment Grid */}
          <div className="mb-8">
            <EquipmentGrid
              equipment={equipmentData}
              onEquipmentAction={handleEquipmentAction}
              onMaintenanceSchedule={handleMaintenanceSchedule}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default OperationsCommandCenter;